/**
 * ProjectorToggle.jsx — 2 boutons : « Projecteur » et « Tablette ».
 *
 *  • Projecteur : plein écran navigateur (F11-like) + `body.c2r-fullscreen`.
 *      Idéal pour vidéoprojecteur en salle. Sticky, footer, distractions masqués.
 *      Sortie via ESC natif ou bouton Quitter.
 *
 *  • Tablette   : `body.c2r-tablet` (pas de plein écran, mais UX optimisée) :
 *      cibles tactiles plus grandes, texte confortable en lecture main-tenue,
 *      layout plus aéré, retour permanent visible.
 *      Sortie via clic sur le bouton Tablette (toggle).
 *
 * Usage : <ProjectorToggle testid="proj-btn-voiturette-hub" />
 */
import React, { useEffect, useState } from 'react';
import { Presentation, Tablet, TabletSmartphone } from 'lucide-react';
import { Button } from './ui/button';
import { FullscreenOverlay, enterFullscreen } from './FullscreenMode';

const TABLET_CLASS = 'c2r-tablet';

function setTabletMode(enabled) {
  if (typeof document === 'undefined') return;
  document.body.classList.toggle(TABLET_CLASS, enabled);
  try {
    if (enabled) localStorage.setItem('c2r:tablet-mode', '1');
    else localStorage.removeItem('c2r:tablet-mode');
  } catch (_) {}
}

export function ProjectorToggle({ testid, className = '' }) {
  const [fs, setFs] = useState(false);
  const [tablet, setTablet] = useState(false);

  // Restaure l'état Tablette au montage si l'utilisateur l'avait activé
  // OU si l'URL contient `?mode=tablette` (deep-link depuis QR élève).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const urlMode = new URLSearchParams(window.location.search).get('mode');
      const saved = localStorage.getItem('c2r:tablet-mode');
      if (urlMode === 'tablette' || saved === '1') {
        setTablet(true);
        setTabletMode(true);
      }
    } catch (_) {}
    return () => setTabletMode(false); // nettoyage à la sortie de la page
  }, []);

  const handleProjector = () => {
    // Le mode projecteur désactive le mode tablette (l'un ou l'autre)
    if (tablet) { setTablet(false); setTabletMode(false); }
    enterFullscreen(() => setFs(true));
  };
  const handleTablet = () => {
    const next = !tablet;
    setTablet(next);
    setTabletMode(next);
  };
  const handleExitFs = () => setFs(false);

  return (
    <>
      <div className={`inline-flex items-center gap-1 ${className}`}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleTablet}
          className={`transition ${tablet
            ? 'bg-sky-500 border-sky-400 text-white hover:bg-sky-400'
            : 'border-sky-500/40 text-sky-400 hover:text-sky-300 hover:bg-sky-500/10 hover:border-sky-500/60'}`}
          data-testid={(testid || 'projector-toggle') + '-tablet'}
          data-state={tablet ? 'on' : 'off'}
          aria-label={tablet ? 'Désactiver le mode Tablette' : 'Activer le mode Tablette élève'}
          aria-pressed={tablet}
          title={tablet ? 'Mode Tablette actif — cliquer pour quitter' : 'Mode Tablette élève (touches plus grandes, texte lisible)'}
        >
          <Tablet className="w-4 h-4 sm:mr-1.5" />
          <span className="hidden sm:inline">Tablette</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleProjector}
          className="border-red-500/40 text-red-400 hover:text-red-300 hover:bg-red-500/10 hover:border-red-500/60"
          data-testid={(testid || 'projector-toggle') + '-projector'}
          aria-label="Activer le mode projecteur plein écran"
          title="Mode projecteur (plein écran)"
        >
          <Presentation className="w-4 h-4 sm:mr-1.5" />
          <span className="hidden sm:inline">Projecteur</span>
        </Button>
      </div>
      {fs && <FullscreenOverlay onExit={handleExitFs} />}
    </>
  );
}

export default ProjectorToggle;
